/* eslint-disable */

import MultiProgress from 'react-multi-progress';
import styles from './stats-overview.module.scss';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DonutGraph, StatsOverviewSkeleton, Shimmer } from '@flexo/atoms';
import { capitalizeFirstLetter, energyBreakdownSuffix, formatToMaxFourDigits, getNumberFormatLocale } from '@flexo/general';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useKpiData, useThrottledHaptics, useThrottledTouch } from '@flexo/hooks';
import { App as CapApp } from '@capacitor/app';

interface StatsOverviewProps {
  title: string;
  type?: string;
  dataInTimeSpan?: boolean;
  view?: string;
  values?: Array<{
    label: string;
    totalPercentage: string;
    totalEnergyValue: string;
    id: string;
    progress?: Array<{
      value: string;
      color: string;
    }>;
  }>;
  graphDataKeys: Array<{
    key: string;
    label: string;
    percentage?: string;
  }>;
  graphColors?: string[];
  totalValue: string;
  isWeb?: boolean;
  count?: number;
}

export function StatsOverview(props: StatsOverviewProps) {
  const {
    StatsOverview__PieContainer,
    StatsOverview__Stats,
    StatsOverview__Stats__Title,
    StatsOverview__Stats__Percentages,
    StatsOverview__Stats__Percentages__Labels,
    StatsOverview__Stats__Percentages__Labels__Label,
  } = styles;

  const ENV = (import.meta as any).env;

  const { t } = useTranslation();
  const kpi = useSelector((state: any) => state.kpi);

  const { title, dataInTimeSpan = true } = props;
  const isWeb = ENV?.VITE_APP_IS_WEB ;

  const ThemeStore = useSelector((state: any) => state.theme);
  const activeTouchRef = useRef<string | null>(null);
  const lastTouchTimeRef = useRef<number>(0);
  const isProcessingTouchRef = useRef<boolean>(false);
  const processingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const appStateListenerRef = useRef<any>(null);
  const vibrate = useThrottledHaptics();
  const shouldAllowTouch = useThrottledTouch(350);
  const [selected, setSelected] = useState<string | null>(null);
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];

  let entity = 'Site';
  let kpiContext = 'Energy';

  if (pathSegment?.includes('economics')) {
    kpiContext = 'Economics';
  }

  if (pathSegment?.includes('community')) {
    entity = 'Community';
  }
  if (pathSegment?.includes('member')) {
    entity = 'Member';
  }

  const overviewKpiApiData =
    (kpi[`${kpiContext}${entity}`]?.['1day']?.[0]?.kpi as any) || null;
  const LOADING_STATE = kpi?.[`${kpiContext}KpiData${entity}AsyncStatus`];

  // Use the new hook for KPI data access
  const { findApiValue, findApiUnit } = useKpiData(
    overviewKpiApiData,
    kpiContext.toLowerCase()
  );

  const graphData = props.graphDataKeys.map((data, index) => ({
    id: data.key,
    label: data.label,
    value: isWeb
      ? findApiValue(data.percentage || '')
      : findApiValue(data.key)
      ? findApiValue(data.key)
      : index === 0
      ? 100
      : 0,
    formattedValue: !overviewKpiApiData ? '-.-' : findApiValue(data.key),
  }));

  // FLEXO-6133 #2 related: a KPI missing from the payload must render "-.-",
  // not "0.00" — findApiValue alone cannot distinguish the two (returns 0).
  const hasApiKpi = (key: string) =>
    !!(overviewKpiApiData as any[])?.some?.((k: any) => k?.name === key);

  let displayItems;
  if (isWeb) {
    // CM-5110 #3: the percentage KPI and the energy-value KPI are computed
    // independently upstream and can disagree (e.g. exported 13% shown vs
    // 15.5% actual). Derive each row's percentage from the energy values that
    // are actually displayed, so badge and value can never contradict each
    // other; fall back to the percentage KPI when any row value is missing.
    const rowEnergyNumbers = graphData.map((data) =>
      !hasApiKpi(data.id)
        ? null
        : typeof data.formattedValue === 'number'
        ? data.formattedValue
        : Number(data.formattedValue) || 0
    );
    const rowEnergySum = rowEnergyNumbers.every((v) => v !== null)
      ? (rowEnergyNumbers as number[]).reduce((a, b) => a + b, 0)
      : null;
    const derivedPercentages =
      rowEnergySum !== null && rowEnergySum > 0
        ? rowEnergyNumbers.map((v) =>
            Math.round(((v as number) / rowEnergySum) * 100)
          )
        : null;

    displayItems = graphData.map((data, itemIndex) => {
      // `data` is the current item like "Self-consumption"
      const individualProgressSegments = graphData.map(
        (segment, segmentIndex) => {
          const colorKey = props.graphColors?.[segmentIndex];
          let segmentColor;
          if (segmentIndex === itemIndex) {
            // This is the segment for the current row
            segmentColor =
              (colorKey ? ThemeStore.colors[colorKey] : undefined) || '#E0E0E0'; // Actual color
          } else {
            // Other segments
            segmentColor = '#F0F0F0'; // Background/neutral color
          }
          return {
            value: segment.value, // All segments retain their original value
            color: segmentColor,
            className: styles.StatsOverview__MultiProgress__Element,
          };
        }
      );

      return {
        label: data.label,
        totalPercentage: !overviewKpiApiData
          ? '-'
          : derivedPercentages
          ? derivedPercentages[itemIndex]
          : data.value, // Fallback: percentage KPI from findApiValue(data.percentage)
        totalPercentageNumber: !overviewKpiApiData
          ? 0
          : derivedPercentages
          ? derivedPercentages[itemIndex] ?? 0
          : Number(data.value) || 0,
        totalEnergyValue: data.formattedValue,
        totalEnergyValueNumber: rowEnergyNumbers[itemIndex],
        // On web `data.id` is the KPI name (graphDataKeys key), so the unit
        // can be resolved here once instead of at render time.
        unit: findApiUnit(data.id),
        id: data.id,
        progress: individualProgressSegments,
      };
    });
  } else {
    displayItems = props.values?.map((value) => ({
      label: value.label,
      totalPercentage: !overviewKpiApiData
        ? '-'
        : findApiValue(value.totalPercentage)?.toLocaleString(getNumberFormatLocale()),
      totalPercentageNumber: Number(findApiValue(value.totalPercentage)) || 0,
      totalEnergyValue: formatToMaxFourDigits(
        findApiValue(value.totalEnergyValue)
      ),
      totalEnergyValueNumber: hasApiKpi(value.totalEnergyValue)
        ? Number(findApiValue(value.totalEnergyValue)) || 0
        : null,
      // FLEXO-6133 #2: `value.id` is a widget-local identifier the KPI lookup
      // misses — the KPI name lives in `value.totalEnergyValue` (config key).
      unit: findApiUnit(value.totalEnergyValue),
      id: value.id,
      progress: value?.progress?.map((progress) => ({
        value: findApiValue(progress.value),
        color: ThemeStore.colors[progress.color],
        className: styles.StatsOverview__MultiProgress__Element,
      })),
    }));
  }

  // Check if all values are 0 or null to disable interactions
  const allValuesZeroOrNull = displayItems?.every((item) => {
    const pctRaw = (item as any).totalPercentageNumber ?? item.totalPercentage;
    const engRaw = (item as any).totalEnergyValueNumber ?? item.totalEnergyValue;
    const pct = typeof pctRaw === 'string' ? Number(pctRaw.replace(',', '.')) : pctRaw;
    const eng = typeof engRaw === 'string' ? Number(engRaw.replace(',', '.')) : engRaw;

    const pctInvalid = pct === 0 || pct === null || pct === undefined || Number.isNaN(pct);
    const pctMissing = item.totalPercentage === '-' || item.totalPercentage === '-.-';
    const engInvalid = eng === 0 || eng === null || eng === undefined || Number.isNaN(eng);
    const engMissing = item.totalEnergyValue === '-' || item.totalEnergyValue === '-.-';

    return (pctInvalid || pctMissing) && (engInvalid || engMissing);
  }) ?? true;

  const isItemNonInteractive = (item: any) => {
    const pctRaw = item.totalPercentageNumber ?? item.totalPercentage;
    const pct = typeof pctRaw === 'string' ? Number(pctRaw.replace(',', '.')) : pctRaw;
    return (
      pct === 0 ||
      pct === null ||
      pct === undefined ||
      Number.isNaN(pct) ||
      item.totalPercentage === '-' ||
      item.totalPercentage === '-.-'
    );
  };

  const endHold = useCallback((source?: 'touch' | 'mouse') => {
    if (source === 'touch') {
      activeTouchRef.current = null;
      lastTouchTimeRef.current = Date.now(); // Record when touch ended
      isProcessingTouchRef.current = false; // Allow new touches
      
      // Clear safety timeout
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
    }
    
    if (isMountedRef.current) {
      setSelected(null);
    }
  }, []);

  const startHold = (valueItem, source: 'touch' | 'mouse') => {
    if (allValuesZeroOrNull || isItemNonInteractive(valueItem)) return;
    
    // Prevent mouse events within 500ms after a touch event (mobile browser quirk)
    if (source === 'mouse') {
      const timeSinceLastTouch = Date.now() - lastTouchTimeRef.current;
      if (timeSinceLastTouch < 500) {
        return;
      }
    }
    
    if (source === 'touch') {
      if (!shouldAllowTouch()) {
        return; // Throttled (expected)
      }
      
      // Prevent rapid successive touches to avoid overwhelming the system
      if (isProcessingTouchRef.current) {
        return;
      }
      
      isProcessingTouchRef.current = true;
      activeTouchRef.current = valueItem.id;
      lastTouchTimeRef.current = Date.now(); // Record when touch started
      
      // Safety timeout to reset processing flag if touch end is missed
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
      processingTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          isProcessingTouchRef.current = false;
        }
        processingTimeoutRef.current = null;
      }, 1000); // Reset after 1 second max
      
      // Throttle haptic feedback to prevent system overload
      vibrate();
    }
    
    if (isMountedRef.current) {
      setSelected(valueItem.id);
    }
  };

  useEffect(() => {
    if (!isWeb) {
      const handleContextMenu = (event) => event.preventDefault();
      const handleCopy = (event) => event.preventDefault();
      const handlePaste = (event) => event.preventDefault();

      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('copy', handleCopy);
      document.addEventListener('paste', handlePaste);

      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('copy', handleCopy);
        document.removeEventListener('paste', handlePaste);
      };
    }
  }, [isWeb]);

  // Handle app background
  useEffect(() => {
    const handleAppStateChange = ({ isActive }: { isActive: boolean }) => {
      if (!isActive) {
        console.log('[StatsOverview] 📱 App going to background - clearing state');
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
          processingTimeoutRef.current = null;
        }
        activeTouchRef.current = null;
        isProcessingTouchRef.current = false;
        if (isMountedRef.current) {
          setSelected(null);
        }
      } else {
        console.log('[StatsOverview] 📱 App resumed to foreground');
      }
    };

    CapApp.addListener('appStateChange', handleAppStateChange).then((listener) => {
      appStateListenerRef.current = listener;
    });

    return () => {
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove();
      }
    };
  }, []);

  // Global touch end listener to handle cases where element re-renders during touch
  useEffect(() => {
    const handleGlobalTouchEnd = () => {
      if (activeTouchRef.current !== null) {
        endHold('touch');
      }
    };

    const handleGlobalTouchCancel = () => {
      if (activeTouchRef.current !== null) {
        endHold('touch');
      }
    };

    document.addEventListener('touchend', handleGlobalTouchEnd);
    document.addEventListener('touchcancel', handleGlobalTouchCancel);

    return () => {
      console.log('[StatsOverview] 🔄 Component unmounting - cleanup');
      isMountedRef.current = false;
      
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('touchcancel', handleGlobalTouchCancel);
      
      // Cleanup processing timeout on unmount
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
    };
  }, [endHold]);



  const rootClassName = isWeb === true || isWeb === 'true'
    ? `${styles.StatsOverview} ${styles.StatsOverview__Web}`
    : styles.StatsOverview;
  const itemMarginTop = isWeb ? '.75em' : '.5em';



  if (LOADING_STATE === 'PENDING' && !overviewKpiApiData && !isWeb) {
     return (
      <StatsOverviewSkeleton
        title={title}
        count={props.values?.length || props.count || 3}
      />
    );
  }

  return (
    <div className={rootClassName} style={{ position: 'relative' }}>
      {LOADING_STATE === 'PENDING' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 999999,
            overflow: 'hidden',
          }}
        >
          <div
            style={
              ENV.VITE_APP_IS_WEB
                ? {
                    width: 'calc(100% - 5px)',
                    height: 'calc(100% - 20px)',
                    position: 'absolute',
                    top: 5,
                    left: 5,
                  }
                : {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: -10,
                    right: -10,
                  }
            }
          >
            <Shimmer />
          </div>
        </div>
      )}
      <div className={StatsOverview__PieContainer}>
        <DonutGraph
          dataInTimeSpan={dataInTimeSpan}
          width={105}
          height={105}
          graphData={graphData}
          colors={
            graphData && graphData.length > 0 && graphData[0].value === 100
              ? props?.graphColors?.map((color, index) => {
                  const anyOtherNotHundred = graphData
                    ?.slice(1)
                    .some((item) => item.value !== 100);
                  if (index === 0 && anyOtherNotHundred) {
                    return ThemeStore.colors[color] || '#CCCCCC';
                  }
                  return index === 0 ? '#F0F0F0' : '#FFFFFF';
                }) ??
                (props.graphDataKeys && props.graphDataKeys.length > 0
                  ? ['#F0F0F0']
                  : []) // Fallback for loading colors
              : props?.graphColors?.map(
                  (color) => ThemeStore.colors[color] || '#CCCCCC'
                ) ?? [] // Fallback for theme colors
          }
          totalAmount={
            !overviewKpiApiData
              ? '-.-'
              : formatToMaxFourDigits(findApiValue(props.totalValue))
          }
          keys={
            props.graphDataKeys.map(
              (k: { key: string; label: string; percentage?: string }) => k.key
            ) as string[]
          }
          totalAmounttype={
            findApiUnit(props.totalValue) === ''
              ? 'kWh'
              : findApiUnit(props.totalValue)
          }
          selectedId={allValuesZeroOrNull ? null : selected}
          setSelectedId={setSelected}
        />
      </div>{' '}
      <div
        className={StatsOverview__Stats}
        style={{
          marginTop: itemMarginTop,
        }}
      >
        <span className={`${StatsOverview__Stats__Title} heading4`}>
          {capitalizeFirstLetter(t(title))}
        </span>
        {displayItems?.map((value: any, index) => (
          <div
            className={StatsOverview__Stats__Percentages}
            key={isWeb ? `${value.label}-${index}` : value.id || value.label}
            style={{
              marginTop: itemMarginTop,
              cursor: allValuesZeroOrNull ? 'default' : 'pointer',
            }}
          {...(!allValuesZeroOrNull && {
              onTouchStart: (e) => {
                e.preventDefault();
                startHold(value, 'touch');
              },
              onTouchEnd: (e) => {
                e.preventDefault();
                endHold('touch');
              },
              onTouchCancel: (e) => {
                e.preventDefault();
                endHold('touch');
              },
              onMouseEnter: () => startHold(value, 'mouse'),
              onMouseLeave: () => endHold('mouse'),
            })}
          >
            <div
              className={`${StatsOverview__Stats__Percentages__Labels} paragraph ${
                styles.noSelect
              } ${selected === value.id ? styles.selected : ''}`}
            >
              <span
                className={`${StatsOverview__Stats__Percentages__Labels__Label} ${styles.noSelect}`}
              >
                {t(value.label)}
              </span>
              <span
                className={`${StatsOverview__Stats__Percentages__Labels__Label} heading5L ${
                  selected === value.id ? styles.selected : ''
                }`}
              >
                {value.totalPercentage}%
                {isWeb && !overviewKpiApiData
                  ? '| -.- ' + value.unit
                  : // FLEXO-6133 #2: format from the raw number —
                    // totalEnergyValue can be an already-localized string,
                    // and a thousands separator coerces it to NaN.
                    energyBreakdownSuffix(
                      value.totalEnergyValueNumber,
                      value.unit
                    )}
              </span>
            </div>

            <div>
              {' '}
              <MultiProgress
                transitionTime={0.5}
                elements={value.progress}
                backgroundColor="#F0F0F0"
                className={`${
                  isWeb ? styles.StatsOverview__MultiProgress : ''
                } ${styles.StatsOverview__MultiProgress__Element}`}
                key={`${value.label}-progress-${index}`}
              />{' '}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default StatsOverview;
